(() => {
  'use strict';

  const COOKIE_NAME = 'tml_sidebar_form';
  const COOKIE_VALUE = 'submitted';
  const COOKIE_HOURS = 1;

  // Deliberately no "modal shown" state. Writing one on display would let the first tab
  // that renders the modal suppress it in every other tab. This state ends on user action.
  const MEMBER_VALUE = 'Direct Access Member - show modal';
  const RELOADED_VALUE = 'Direct Access Member - page reloaded';
  const CLOSED_VALUE = 'Direct Access Member - modal closed';
  const ACTIVE_VALUE = 'Direct Access Member - no modal needed';

  const PENDING_VALUES = [COOKIE_VALUE, MEMBER_VALUE];
  const POST_SUBMIT_VALUES = [COOKIE_VALUE, MEMBER_VALUE, RELOADED_VALUE, CLOSED_VALUE, ACTIVE_VALUE];

  const MEMBER_CHECK_PATH = 'search?view=tml-member';
  const MEMBER_CHECK_INTERVAL_MS = 30000;
  const MEMBER_CHECK_TIMEOUT_MS = 10000;

  const SIDEBAR_TAB_SELECTOR = '.da-sidebar-tab-direct-access-sidebar';
  const HIDDEN_CLASS = 'hidden';

  const MODAL_ID = 'tml-member-modal';
  const OVERLAY_ID = 'tml-member-overlay';
  const OPEN_CLASS = 'is-open';
  const BODY_MODAL_CLASS = 'tml-modal-active';

  const REGISTRATION_NOTICE_ID = 'tml-registration-success-notice';
  const VERIFYING_NOTICE_ID = 'tml-verifying-notice';
  const VISIBLE_CLASS = 'is-visible';

  const MEMBER_CHANNEL_NAME = 'tml-member-modal';
  const MEMBER_CLOSED_MESSAGE = 'member-modal-closed';

  const member_channel = 'BroadcastChannel' in window ? new BroadcastChannel(MEMBER_CHANNEL_NAME) : null;

  const setCookie = (name, value, hours) => {
    const expires_at = new Date(Date.now() + hours * 60 * 60 * 1000);

    document.cookie = `${name}=${value}; expires=${expires_at.toUTCString()}; path=/; SameSite=Lax`;
  };

  const getCookie = (name) => {
    const prefix = `${name}=`;
    const entry = document.cookie.split('; ').find((item) => item.startsWith(prefix));

    return entry ? entry.slice(prefix.length) : '';
  };

  const hasSubmittedCookie = () => getCookie(COOKIE_NAME) === COOKIE_VALUE;

  const hasPostSubmitCookie = () => POST_SUBMIT_VALUES.includes(getCookie(COOKIE_NAME));

  const isLoggedIn = () => document.body.classList.contains('customer-logged-in');

  const hasRenderedDirectAccess = () => document.body.dataset.directAccess === '1';

  const hideSidebarTab = () => {
    const tabs = document.querySelectorAll(SIDEBAR_TAB_SELECTOR);

    if (!tabs.length) return;

    tabs.forEach((tab) => tab.classList.add(HIDDEN_CLASS));
  };

  const hideSidebarTabIfSubmitted = () => {
    if (!hasPostSubmitCookie()) return;

    hideSidebarTab();
  };

  const showVerifyingNoticeIfWaiting = () => {
    const notice = document.getElementById(VERIFYING_NOTICE_ID);

    if (!notice) return;
    if (notice.classList.contains(VISIBLE_CLASS)) return;
    if (!hasSubmittedCookie()) return;

    const registration_notice = document.getElementById(REGISTRATION_NOTICE_ID);

    if (registration_notice?.classList.contains(VISIBLE_CLASS)) return;

    notice.classList.add(VISIBLE_CLASS);
  };

  const MODAL_FALLBACK = {
    title: "Congratulations, you've got Direct Access!",
    text: '<p>Your TML Direct Access membership is now active. Refresh the page to see your member pricing and unlocked content.</p>',
    reload_label: 'Refresh page',
    later_label: "I'll refresh later"
  };

  const withModalFallbacks = (copy) => ({
    title: copy?.title || MODAL_FALLBACK.title,
    text: copy?.text || MODAL_FALLBACK.text,
    reload_label: copy?.reload_label || MODAL_FALLBACK.reload_label,
    later_label: copy?.later_label || MODAL_FALLBACK.later_label
  });

  const memberModalMarkup = (copy) => `
    <div class="tml-modal-overlay" id="${OVERLAY_ID}"></div>
    <div class="tml-modal" id="${MODAL_ID}" role="dialog" aria-modal="true" aria-labelledby="tml-member-title">
      <div class="tml-modal-box">
        <h2 class="tml-modal-title" id="tml-member-title">${copy.title}</h2>
        <div class="tml-modal-text">${copy.text}</div>
        <div class="tml-modal-actions">
          <button type="button" class="tml-modal-btn tml-modal-btn--primary" data-tml-member-action="reload">${copy.reload_label}</button>
          <button type="button" class="tml-modal-btn tml-modal-btn--secondary" data-tml-member-action="later">${copy.later_label}</button>
        </div>
      </div>
    </div>
  `;

  const closeMemberModal = () => {
    const modal = document.getElementById(MODAL_ID);
    const overlay = document.getElementById(OVERLAY_ID);

    if (modal) modal.classList.remove(OPEN_CLASS);
    if (overlay) overlay.classList.remove(OPEN_CLASS);

    document.body.style.overflow = '';
    document.body.classList.remove(BODY_MODAL_CLASS);
  };

  const handleMemberAction = (action) => {
    const cookie_value = action === 'reload' ? RELOADED_VALUE : CLOSED_VALUE;

    setCookie(COOKIE_NAME, cookie_value, COOKIE_HOURS);
    closeMemberModal();
    member_channel?.postMessage(MEMBER_CLOSED_MESSAGE);

    if (action === 'reload') window.location.reload();
  };

  const handleChannelMessage = (event) => {
    if (event.data !== MEMBER_CLOSED_MESSAGE) return;

    const modal = document.getElementById(MODAL_ID);

    if (!modal?.classList.contains(OPEN_CLASS)) return;

    closeMemberModal();
  };

  const buildMemberModal = (copy) => {
    if (document.getElementById(MODAL_ID)) return;

    document.body.insertAdjacentHTML('beforeend', memberModalMarkup(copy));

    const buttons = document.querySelectorAll(`#${MODAL_ID} [data-tml-member-action]`);

    buttons.forEach((button) => {
      button.addEventListener('click', () => handleMemberAction(button.dataset.tmlMemberAction));
    });
  };

  const openMemberModal = (copy) => {
    // The page already renders with access, so there is nothing to refresh.
    if (hasRenderedDirectAccess()) return;

    // A background tab would open the modal to an empty room. The cookie stays pending,
    // so it opens in whichever tab the user actually comes back to.
    if (document.visibilityState !== 'visible') return;

    buildMemberModal(withModalFallbacks(copy));

    const modal = document.getElementById(MODAL_ID);
    const overlay = document.getElementById(OVERLAY_ID);

    if (!modal || !overlay) return;

    modal.classList.add(OPEN_CLASS);
    overlay.classList.add(OPEN_CLASS);

    document.body.style.overflow = 'hidden';
    document.body.classList.add(BODY_MODAL_CLASS);
  };

  const openMemberModalIfPending = async () => {
    if (getCookie(COOKIE_NAME) !== MEMBER_VALUE) return;

    const status = await fetchMemberStatus();

    if (!status || status.direct_access_member !== true) return;

    openMemberModal(status.modal);
  };

  const settleIfAlreadyMember = () => {
    if (!hasRenderedDirectAccess()) return false;
    if (!PENDING_VALUES.includes(getCookie(COOKIE_NAME))) return false;

    setCookie(COOKIE_NAME, ACTIVE_VALUE, COOKIE_HOURS);

    return true;
  };

  const localeRoot = () => {
    const root = window.theme?.routes?.root_url || window.Shopify?.routes?.root || '/';

    return root.endsWith('/') ? root : `${root}/`;
  };

  const memberCheckUrl = () => `${localeRoot()}${MEMBER_CHECK_PATH}&_=${Date.now()}`;

  const fetchMemberStatus = async () => {
    const controller = new AbortController();
    const timeout_id = setTimeout(() => controller.abort(), MEMBER_CHECK_TIMEOUT_MS);

    try {
      const response = await fetch(memberCheckUrl(), {
        cache: 'no-store',
        credentials: 'same-origin',
        signal: controller.signal
      });

      if (!response.ok) return null;

      return JSON.parse(await response.text());
    } catch (error) {
      return null;
    } finally {
      clearTimeout(timeout_id);
    }
  };

  let member_check_timer = null;
  let member_check_in_flight = false;

  const stopMemberPolling = () => {
    if (!member_check_timer) return;

    clearInterval(member_check_timer);
    member_check_timer = null;
  };

  const checkMemberStatus = async () => {
    if (!hasSubmittedCookie()) {
      stopMemberPolling();
      openMemberModalIfPending();
      return;
    }

    if (member_check_in_flight) return;

    member_check_in_flight = true;

    try {
      const status = await fetchMemberStatus();

      if (!status || status.direct_access_member !== true) return;

      stopMemberPolling();
      setCookie(COOKIE_NAME, MEMBER_VALUE, COOKIE_HOURS);

      openMemberModal(status.modal);
    } finally {
      member_check_in_flight = false;
    }
  };

  const handleVisibilityChange = () => {
    if (document.visibilityState !== 'visible') return;

    showVerifyingNoticeIfWaiting();
    openMemberModalIfPending();

    if (member_check_timer) checkMemberStatus();
  };

  const startMemberPollingIfSubmitted = () => {
    if (!isLoggedIn() || !hasSubmittedCookie()) return;

    checkMemberStatus();

    member_check_timer = setInterval(checkMemberStatus, MEMBER_CHECK_INTERVAL_MS);
  };

  const isModalRegisterForm = (form) => {
    if (!form || !form.classList || !form.classList.contains('tml-form')) return false;

    return !!form.closest('.tml-signup-modal');
  };

  // Never call form.requestSubmit() or form.submit() from here. While the submit event is
  // dispatching, the form has its "firing submission events" flag raised, so a second
  // submission from inside the listener is silently ignored and the user has to click twice.
  // Write the cookie and let the event carry on to the listener in tml-register-form.liquid.
  const handleModalSubmit = (event) => {
    const form = event.target;

    if (!isModalRegisterForm(form)) return;
    if (hasSubmittedCookie()) return;

    setCookie(COOKIE_NAME, COOKIE_VALUE, COOKIE_HOURS);

    if (!hasSubmittedCookie()) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    hideSidebarTab();
  };

  const init = () => {
    hideSidebarTabIfSubmitted();

    if (settleIfAlreadyMember()) return;

    showVerifyingNoticeIfWaiting();
    openMemberModalIfPending();
    startMemberPollingIfSubmitted();
  };

  document.addEventListener('submit', handleModalSubmit, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('visibilitychange', handleVisibilityChange);

  member_channel?.addEventListener('message', handleChannelMessage);

  document.addEventListener('shopify:section:load', hideSidebarTabIfSubmitted);

  window.TML = window.TML || {};
  window.TML.setCookie = setCookie;
  window.TML.getCookie = getCookie;
})();
